// src/pages/RelatorioEncapsulacao.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  TextField, Button, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, TablePagination
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Medication as CapsuleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const RelatorioEncapsulacao = () => {
  const { api } = useAuth();

  // Suprimir erro do proxy (extensões do browser)
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0]?.toString().includes('disconnected port object')) {
        return; // Ignorar erro do proxy
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [estatisticas, setEstatisticas] = useState(null);
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    funcionario: '',
    quantidadeCapsulas: ''
  });
  const [funcionarios, setFuncionarios] = useState([]);
  const [dadosDetalhados, setDadosDetalhados] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [distribuicao, setDistribuicao] = useState([]);

  // Definir período padrão (mês atual)
  useEffect(() => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

    setFiltros(prev => ({
      ...prev,
      dataInicio: primeiroDia.toISOString().split('T')[0],
      dataFim: ultimoDia.toISOString().split('T')[0]
    }));
  }, []);

  const carregarDistribuicao = useCallback(async () => {
    try {
      const parametros = {
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim,
        setor: 'Encapsulação'
      };

      if (filtros.funcionario) {
        parametros.funcionario = filtros.funcionario;
      }

      console.log('📊 Carregando distribuição por quantidade...');
      const response = await api.relatorioVolumeProducaoDistribuicao(parametros);

      if (response && response.success) {
        console.log('✅ Distribuição carregada:', response.dados);
        setDistribuicao(response.dados || []);
      } else {
        console.warn('⚠️ Erro ao carregar distribuição:', response?.message);
        setDistribuicao([]);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar distribuição:', error);
      setDistribuicao([]);
    }
  }, [api, filtros]);

  const carregarDados = useCallback(async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o período (data início e fim)');
      return;
    }

    console.log('🔍 Filtros atuais:', filtros);

    if (loading) {
      console.log('⚠️ Carregamento já em andamento, ignorando nova chamada');
      return;
    }

    try {
      setLoading(true);
      setError('');
      console.log('📊 Iniciando carregamento de dados...');

      const parametros = {
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim,
        setor: 'Encapsulação',
        limite: 50000
      };

      if (filtros.quantidadeCapsulas) {
        parametros.quantidade = filtros.quantidadeCapsulas;
      }

      if (filtros.funcionario) {
        parametros.funcionario = filtros.funcionario;
      }

      console.log('📦 Carregando dados detalhados para Encapsulação');
      console.log('📅 Período:', parametros.dataInicio, 'até', parametros.dataFim);
      console.log('⚙️ Parâmetros completos:', parametros);

      const response = await api.relatorioVolumeProducao(parametros);
      console.log('📡 Resposta da API:', response);

      if (response && response.success) {
        console.log('✅ API retornou sucesso');
        const leituras = response.dados || [];
        console.log(`📋 Total de leituras recebidas: ${leituras.length}`);

        setDadosDetalhados(leituras);

        const estatisticas = response.meta?.estatisticas || {};
        setEstatisticas(estatisticas);

        console.log(`✅ Carregadas ${leituras.length} requisições`);
        console.log('📊 Total de cápsulas:', estatisticas.total_capsulas);

        await carregarDistribuicao();
      } else {
        console.error('❌ Erro na resposta da API');
        const mensagemErro = response?.message || response?.error || 'Erro ao carregar dados';
        setError(mensagemErro);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar relatório:', error);
      setError(`Erro ao carregar dados: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
      console.log('✅ Carregamento finalizado');
    }
  }, [filtros, api, carregarDistribuicao, loading]);

  const carregarFuncionarios = useCallback(async () => {
    try {
      console.log('🔄 Carregando funcionários...');
      const response = await api.listarFuncionarios();
      console.log('📡 Resposta API funcionários:', response);
      if (response.success) {
        const funcionariosData = response.dados || [];
        console.log('👥 Funcionários carregados:', funcionariosData.length);
        setFuncionarios(funcionariosData);
      } else {
        console.error('❌ Erro na API de funcionários:', response.message);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar funcionários:', error);
    }
  }, [api]);

  useEffect(() => {
    carregarFuncionarios();
  }, [carregarFuncionarios]);

  // Auto-carregar dados quando filtros de data estiverem prontos
  useEffect(() => {
    if (filtros.dataInicio && filtros.dataFim) {
      const timer = setTimeout(() => {
        carregarDados();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [filtros.dataInicio, filtros.dataFim, carregarDados]);

  return (
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={0} sx={{
        p: 3, mb: 3,
        background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
        color: 'white',
        borderRadius: 2
      }}>
        <Box display="flex" alignItems="center">
          <CapsuleIcon sx={{ mr: 2, fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Encapsulação - Volume de Produção
            </Typography>
            <Typography variant="subtitle1" opacity={0.9}>
              Análise detalhada da produção do setor Encapsulação
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtros
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data Início"
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Data Fim"
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Funcionário (Opcional)</InputLabel>
              <Select
                value={filtros.funcionario}
                onChange={(e) => setFiltros(prev => ({ ...prev, funcionario: e.target.value }))}
                label="Funcionário (Opcional)"
              >
                <MenuItem value="">Todos os Funcionários</MenuItem>
                {funcionarios.map(funcionario => (
                  <MenuItem key={funcionario.codigo} value={funcionario.codigo}>
                    {funcionario.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Quantidade Cápsulas (Opcional)"
              type="number"
              value={filtros.quantidadeCapsulas}
              onChange={(e) => setFiltros(prev => ({ ...prev, quantidadeCapsulas: e.target.value }))}
              fullWidth
              placeholder="Ex: 30, 60, 90..."
              helperText="Filtrar por quantidade específica"
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={carregarDados}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
            >
              {loading ? 'Carregando...' : 'Gerar Relatório'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Estatísticas */}
      {estatisticas && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main" fontWeight="bold">
                  {estatisticas.total_leituras || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Requisições com Produção
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {estatisticas.total_capsulas || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de Cápsulas
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {estatisticas.media_capsulas_por_requisicao || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Média por Requisição
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <CapsuleIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Setor: Encapsulação
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Gráfico de Distribuição */}
          {distribuicao.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  📊 Distribuição por Quantidade de Cápsulas
                </Typography>

                {/* Gráfico de Barras Simples */}
                <Box sx={{ height: 300, mt: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                    {distribuicao.slice(0, 10).map((item, index) => (
                      <Box key={item.quantidade} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {item.quantidade}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, position: 'relative' }}>
                          <Box
                            sx={{
                              height: 24,
                              backgroundColor: '#e3f2fd',
                              borderRadius: 1,
                              position: 'relative',
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                backgroundColor: index < 3 ? '#1976d2' : '#42a5f5',
                                width: `${Math.min((item.total_requisicoes / Math.max(...distribuicao.map(d => d.total_requisicoes))) * 100, 100)}%`,
                                borderRadius: 1,
                                transition: 'width 0.3s ease'
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ minWidth: 80, textAlign: 'left' }}>
                          <Typography variant="body2" color="text.secondary">
                            {item.total_requisicoes} req.
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                          <Typography variant="body2" color="success.main" fontWeight="bold">
                            {item.total_capsulas} caps.
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Resumo das Top 5 quantidades */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Top 5 Quantidades Mais Produzidas:
                  </Typography>
                  <Grid container spacing={1}>
                    {distribuicao.slice(0, 5).map((item) => (
                      <Grid item xs={6} sm={4} md={2.4} key={item.quantidade}>
                        <Card variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="h6" color="primary.main" fontWeight="bold">
                            {item.quantidade}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.total_requisicoes} requisições
                          </Typography>
                          <Typography variant="caption" color="success.main" display="block">
                            {item.total_capsulas} cápsulas
                          </Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Tabela Detalhada de Produção */}
      {dadosDetalhados.length > 0 && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight="bold">
              📋 Detalhes de Produção - Encapsulação ({dadosDetalhados.length} registros)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Período: {filtros.dataInicio} até {filtros.dataFim}
              {filtros.quantidadeCapsulas && (
                <> | Filtro: {filtros.quantidadeCapsulas} cápsulas</>
              )}
            </Typography>
          </Box>

          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data/Hora</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Requisição</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Funcionário</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Setor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dadosDetalhados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {item.data_formatada}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.codigo_requisicao}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {item.funcionario_nome || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cód: {item.codigo_funcionario}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${item.quantidade} cáps`}
                        color="success"
                        variant="filled"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.setor_efetivo}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={dadosDetalhados.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 250]}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Paper>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      )}

      {/* Empty State */}
      {!loading && dadosDetalhados.length === 0 && !error && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CapsuleIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhum dado encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ajuste os filtros e tente novamente
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RelatorioEncapsulacao;
